import React, { forwardRef } from "react"
import { Button, ButtonProps } from "antd"

// Define custom props interface extending Ant Design's ButtonProps
interface DataQButtonProps {
  id?: string
  isDisabled?: boolean
  isLoading?: boolean
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void
  type?: //Note: Not all the types work but we can use CSS to style them, left in for future reference
  | "primary"
    | "secondary"
    | "success"
    | "danger"
    | "default"
    | "dashed"
    | "link"
    | "text"
    | "form"
  className?: string
  htmlType?: "button" | "submit" | "reset"
  size?: "large" | "middle" | "small"
  children?: React.ReactNode
}

/**
 * A custom button component with additional props for enhanced functionality and styling.
 * This version forwards refs to work properly with Antd tooltips.
 *
 * Supports custom types: "secondary", "success", "danger", and "form" in addition to standard Ant Design types.
 * Custom styling is applied via CSS classes while maintaining Ant Design compatibility.
 *
 * @param props - The props object containing various configurations for the button.
 * @param ref - Forwarded ref from parent component
 * @returns A React element representing the custom button component.
 */
const DataQButton = forwardRef<
  HTMLButtonElement | HTMLAnchorElement,
  DataQButtonProps
>((props, ref) => {
  const {
    id,
    isDisabled,
    isLoading,
    onClick,
    type,
    className,
    htmlType,
    size,
    children,
  } = props

  // Map custom types to valid Ant Design button types
  const getAntdButtonType = (customType?: string): ButtonProps["type"] => {
    switch (customType) {
      case "form":
        return "primary"
      case "secondary":
        return "default"
      case "success":
        return "default" // Will use CSS for styling
      case "danger":
        return "default" // Will use CSS for styling
      default:
        return customType as ButtonProps["type"]
    }
  }

  return (
    <Button
      ref={ref}
      id={id || ""}
      disabled={isDisabled}
      loading={isLoading}
      onClick={onClick}
      type={getAntdButtonType(type)}
      className={`${className || ""} ${
        type || ""
      } dataq-btn-base pointer-events-auto`}
      htmlType={htmlType}
      size={size}
    >
      {children}
    </Button>
  )
})

// Add display name for better debugging
DataQButton.displayName = "DataQButton"

export default DataQButton
